/**
 * PostHog — product analytics + session replay on errors
 * Lazy init, production only, no PII in events.
 */

const API_KEY = import.meta.env.VITE_POSTHOG_KEY || ''
const API_HOST = 'https://eu.i.posthog.com'

type PostHogInstance = typeof import('posthog-js').default

let ph: PostHogInstance | null = null

export async function initPostHog(): Promise<void> {
  if (!API_KEY || !import.meta.env.PROD) return
  if (ph) return

  const { default: posthog } = await import('posthog-js')

  posthog.init(API_KEY, {
    api_host: API_HOST,
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: false,
    persistence: 'localStorage',
    disable_session_recording: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '[data-sensitive]',
    },
    loaded: (instance) => {
      ph = instance
    },
  })

  ph = posthog
}

export function posthogIdentify(userId: string, props?: Record<string, string | number | boolean>) {
  if (!ph) return
  ph.identify(userId, props)
}

export function posthogReset() {
  ph?.reset()
}

export function posthogCapture(event: string, props?: Record<string, unknown>) {
  if (!ph || !import.meta.env.PROD) return
  ph.capture(event, props)
}

/** Enable session replay — called by Sentry ErrorBoundary on error capture */
export function posthogStartReplayOnError() {
  if (!ph || !import.meta.env.PROD) return
  try {
    ph.startSessionRecording()
  } catch {
    // non-critique
  }
}

// ── Business events ────────────────────────────────────────────────────────────

export const PostHog = {
  // Onboarding funnel
  registerCompleted: (method: string) => posthogCapture('register_completed', { method }),
  petAdded: (petType: string) => posthogCapture('pet_added', { pet_type: petType }),
  vaccineAdded: () => posthogCapture('vaccine_added'),

  // Health
  medicationAdded: () => posthogCapture('medication_added'),
  appointmentCreated: () => posthogCapture('appointment_created'),
  weightRecorded: () => posthogCapture('weight_recorded'),

  // Conversion
  subscriptionUpgraded: (plan: string) => posthogCapture('subscription_upgraded', { plan }),
  dataExported: () => posthogCapture('data_exported'),

  // App
  languageChanged: (lang: string) => posthogCapture('language_changed', { language: lang }),
  pwaInstalled: () => posthogCapture('pwa_installed'),

  // Page views (manual, no autocapture)
  pageViewed: (page: string) => posthogCapture('$pageview', { $current_url: page }),
}
